import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { PageHeader, Loader, Chip } from "@/components/shared";
import { formatINR, formatDate, formatTimeIST } from "@/lib/format";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar, MapPin, Video, Users, Check } from "lucide-react";

export function Workshops() {
  useSeo({
    title: "Constellation Workshops — Upcoming Dates in Ludhiana & Online",
    description: "Upcoming Family Constellation group workshops with Arjunna Soni in Ludhiana and online. See dates, seats remaining, and register your place.",
    path: "/workshops",
  });
  const [upcoming, setUpcoming] = useState(null);
  const [past, setPast] = useState([]);
  useEffect(() => {
    api.get("/workshops", { params: { filter: "upcoming" } }).then((r) => setUpcoming(r.data)).catch(() => setUpcoming([]));
    api.get("/workshops", { params: { filter: "past" } }).then((r) => setPast(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader crumb="Workshops" title="Workshop calendar" intro="Group constellation work — often the most powerful, and most affordable, way to experience the method." />
      <section className="container-page pb-16 md:pb-24">
        <h2 className="text-2xl md:text-3xl text-earth mb-8" style={{ fontWeight: 600 }}>Upcoming Family Constellation workshops</h2>
        {!upcoming ? <Loader /> : upcoming.length === 0 ? (
          <div className="bg-surface-card border hairline rounded-lg p-10 text-center">
            <h3 className="text-2xl text-earth mb-2">No workshops scheduled just now</h3>
            <p className="text-earth/70 mb-5">New dates are added regularly. Leave your email and you'll be the first to know — or book a one-to-one session.</p>
            <Link to="/book" className="btn-gold">Book an individual session</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {upcoming.map((w) => (
              <Link key={w.workshop_id} to={`/workshops/${w.slug}`} className="group bg-surface-card border hairline rounded-lg overflow-hidden hover:border-gold transition-all hover:-translate-y-1" data-testid={`workshop-card-${w.slug}`}>
                <div className="p-7">
                  <div className="flex items-center gap-2 label-caps text-earth/60 mb-3">
                    <Calendar size={14} className="text-gold" /> {formatDate(w.startDate)}
                    <span className="text-gold">•</span>
                    {w.isOnline ? <><Video size={14} /> Online</> : <><MapPin size={14} /> {w.venue?.city}</>}
                  </div>
                  <h3 className="text-2xl text-earth mb-2 group-hover:text-gold-dark transition-colors">{w.title}</h3>
                  <p className="text-earth/70 mb-5">{w.subtitle}</p>
                  <div className="flex items-center justify-between pt-4 border-t hairline">
                    <span className="text-xl text-earth font-display" style={{ fontWeight: 600 }}>{formatINR(w.price)}</span>
                    <span className={`text-sm font-semibold ${w.seatsRemaining === 0 ? "text-destructive" : "text-gold-dark"}`}>
                      {w.seatsRemaining === 0 ? "Fully booked — join waitlist" : w.seatsRemaining <= 5 ? `Only ${w.seatsRemaining} seats left` : "Seats available"}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-20">
            <h2 className="text-2xl md:text-3xl text-earth mb-8" style={{ fontWeight: 600 }}>Past workshops</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map((w) => (
                <div key={w.workshop_id} className="bg-surface-card border hairline rounded-lg p-6 opacity-80">
                  <div className="label-caps text-earth/50 mb-2">{formatDate(w.startDate)}</div>
                  <h3 className="text-xl text-earth">{w.title}</h3>
                  <p className="text-sm text-earth/60 mt-1">{w.isOnline ? "Online" : w.venue?.city}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export function WorkshopDetail() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [nf, setNf] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", city: "", numberOfSeats: 1, consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useSeo({
    title: w ? w.title : "Workshop",
    description: w ? (w.subtitle || w.description) : undefined,
    path: `/workshops/${slug}`,
    type: "event",
    jsonLd: w ? {
      "@context": "https://schema.org", "@type": "Event", name: w.title,
      description: w.subtitle || w.description, startDate: w.startDate, endDate: w.endDate,
      eventAttendanceMode: w.isOnline ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode",
      location: w.isOnline ? { "@type": "VirtualLocation", url: "https://arjunnasoni.in/workshops/" + w.slug }
        : { "@type": "Place", name: w.venue?.name, address: `${w.venue?.name || ""}, ${w.venue?.city || "Ludhiana"}` },
      offers: { "@type": "Offer", price: w.price, priceCurrency: "INR" },
      organizer: { "@type": "Person", name: "Arjunna Soni" },
    } : null,
  });

  const load = () => api.get(`/workshops/${slug}`).then((r) => setW(r.data)).catch(() => setNf(true));
  useEffect(() => { load(); }, [slug]);

  if (nf) return <div className="container-page py-24 text-center text-earth/70">Workshop not found. <Link to="/workshops" className="text-gold-dark underline">All workshops</Link></div>;
  if (!w) return <Loader />;

  const full = w.seatsRemaining === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept the consent checkbox."); return; }
    setBusy(true);
    try {
      if (full) {
        const res = await api.post(`/workshops/${slug}/waitlist`, { fullName: form.fullName, email: form.email, phone: form.phone });
        toast.success(res.data.message);
        setDone({ waitlist: true });
      } else {
        const res = await api.post(`/workshops/${slug}/register`, form);
        setDone(res.data);
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader crumb={w.title} title={w.title} intro={w.subtitle} />
      <section className="container-page pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-4 mb-8 text-earth">
              <span className="flex items-center gap-2 bg-surface-card border hairline rounded-md px-4 py-2"><Calendar size={16} className="text-gold" /> {formatDate(w.startDate)}</span>
              <span className="flex items-center gap-2 bg-surface-card border hairline rounded-md px-4 py-2">{formatTimeIST(w.startDate)} IST</span>
              <span className="flex items-center gap-2 bg-surface-card border hairline rounded-md px-4 py-2">{w.isOnline ? <><Video size={16} className="text-gold" /> Online (link on confirmation)</> : <><MapPin size={16} className="text-gold" /> {w.venue?.name}, {w.venue?.city}</>}</span>
            </div>

            <p className="text-xl text-earth/80 leading-relaxed mb-8">{w.description}</p>

            {w.whoIsItFor?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl text-earth mb-4" style={{ fontWeight: 600 }}>Who it's for</h2>
                <ul className="space-y-3">{w.whoIsItFor.map((x, i) => <li key={i} className="flex gap-3 text-earth/80 text-lg"><Check className="text-gold shrink-0 mt-1" size={18} />{x}</li>)}</ul>
              </div>
            )}
            {w.agenda?.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl text-earth mb-4" style={{ fontWeight: 600 }}>The day</h2>
                <ul>{w.agenda.map((a, i) => <li key={i} className="flex gap-4 py-3 border-b hairline last:border-0"><span className="label-caps text-gold-dark w-16 shrink-0 pt-1">{a.time}</span><span className="text-earth/80">{a.activity}</span></li>)}</ul>
              </div>
            )}
            {w.whatToBring?.length > 0 && (
              <div className="mb-8"><h2 className="text-2xl text-earth mb-4" style={{ fontWeight: 600 }}>What to bring</h2><div className="flex flex-wrap gap-2">{w.whatToBring.map((x, i) => <Chip key={i}>{x}</Chip>)}</div></div>
            )}
            {w.faqs?.length > 0 && (
              <Accordion type="single" collapsible>
                {w.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`wf-${i}`} className="border-b hairline">
                    <AccordionTrigger className="text-left text-earth hover:no-underline py-4">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-earth/75 pb-4">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>

          {/* Registration */}
          <aside className="lg:col-span-5">
            <div className="sticky top-28 bg-surface-card border hairline rounded-lg p-7" data-testid="workshop-register-box">
              <div className="flex items-end justify-between mb-1">
                <div className="text-3xl text-earth font-display" style={{ fontWeight: 600 }}>{formatINR(w.price)}</div>
                <span className={`text-sm font-semibold ${full ? "text-destructive" : "text-gold-dark"}`}>
                  {full ? "Fully booked" : w.seatsRemaining <= 5 ? `Only ${w.seatsRemaining} left` : "Seats available"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-earth/60 mb-5"><Users size={14} /> Capacity {w.capacity}</div>

              {done ? (
                <div className="text-center py-4" data-testid="workshop-success">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-3"><Check className="text-gold-dark" /></div>
                  <h3 className="text-xl text-earth mb-1">{done.waitlist ? "You're on the waitlist" : "You're registered!"}</h3>
                  {done.registrationReference && <p className="text-earth/70 text-sm">Reference: <strong>{done.registrationReference}</strong></p>}
                  <p className="text-earth/70 text-sm mt-2">{done.waitlist ? "We'll be in touch if a seat opens up." : "Arjunna will confirm the details with you shortly."}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="workshop-register-form">
                  <input required placeholder="Full name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className="w-full px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ws-name" />
                  <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ws-email" />
                  <input required placeholder="Phone (+91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ws-phone" />
                  {!full && <>
                    <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ws-city" />
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-earth/70">Seats</label>
                      <input type="number" min="1" max={Math.max(1, w.seatsRemaining)} value={form.numberOfSeats} onChange={(e) => setForm({ ...form, numberOfSeats: parseInt(e.target.value) || 1 })} className="w-20 px-3 py-2 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ws-seats" />
                    </div>
                  </>}
                  <label className="flex items-start gap-2 text-sm text-earth/70 cursor-pointer">
                    <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#C9A84C]" data-testid="ws-consent" />
                    <span>I agree to be contacted about this workshop and accept the terms & privacy policy.</span>
                  </label>
                  <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="ws-submit">
                    {busy ? "…" : full ? "Join the waitlist" : "Register"}
                  </button>
                  <p className="text-xs text-earth/50 text-center">No payment is taken now — this reserves your place as a request.</p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
